/**
 * BusquedaGrupoCampus
 *
 * Tabla de grupos del campus seleccionado (sin restricción por asesor).
 * Filtros:
 *  - Sección Académica (Licenciatura, Preparatoria, Maestría, Doctorado, ...)
 *  - Búsqueda por código de grupo o materia
 *
 * Reutiliza estilos del módulo RegistroCalificacionesScreen.
 */

import React, { useEffect, useState } from 'react';
import styles from '../RegistroCalificacionesScreen/RegistroCalificaciones.module.css';
import { getGruposCampus } from '../../api/calificacionesEscolar';
import { getSections } from '../../api/catalogs';
import { getCampus } from '../../api/campus';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

function BusquedaGrupoCampus({ campusSeleccionado, onSeleccionarGrupo }) {
  const [seccionId, setSeccionId] = useState('');
  const [secciones, setSecciones] = useState([]);
  const [cicloId, setCicloId] = useState('');
  const [ciclos, setCiclos] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [campusData, setCampusData] = useState([]); // Para obtener id_institucion
  const [paginaActual, setPaginaActual] = useState(1);
  const REGISTROS_POR_PAGINA = 15;

  // Cargar catálogo de Campuses (para obtener id_institucion)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getCampus();
        if (cancelled) return;
        setCampusData(data);
      } catch (err) {
        console.error('[BusquedaGrupoCampus] Error campuses:', err);
        setCampusData([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Cargar Secciones Académicas filtradas por institución del campus seleccionado
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Encontrar la institución del campus seleccionado
        let idInstitucion = null;
        if (campusSeleccionado && campusData.length > 0) {
          const campusObj = campusData.find(c => String(c.id) === String(campusSeleccionado));
          if (campusObj && campusObj.institucion) {
            idInstitucion = campusObj.institucion;
          }
        }
        
        // Cargar secciones filtrando por institución
        const data = await getSections(idInstitucion);
        if (cancelled) return;
        
        // Mapear al formato esperado
        const mapped = (data || []).map(s => ({
          id: s.id,
          id_seccion: s.id,
          nombre: s.name,
          nombre_seccion: s.name,
          activo: true
        }));
        setSecciones(mapped);
      } catch (err) {
        console.error('[BusquedaGrupoCampus] Error secciones:', err);
        setSecciones([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [campusSeleccionado, campusData]);

  // Cargar ciclos escolares en orden descendente
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${API_BASE}/catalogos/ciclos-escolares/?ordering=-anio,-numero_periodo&page_size=100`, {
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        });
        if (!res.ok) return;
        const data = await res.json();
        const lista = Array.isArray(data) ? data : (data.results || []);
        if (!cancelled) setCiclos(lista);
      } catch (err) {
        console.error('[BusquedaGrupoCampus] Error ciclos:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-cargar al cambiar campus, sección o ciclo
  useEffect(() => {
    setPaginaActual(1);
    handleBuscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campusSeleccionado, seccionId, cicloId]);

  const handleBuscar = async () => {
    setLoading(true);
    setErrorMessage('');
    try {
      const data = await getGruposCampus({
        id_campus: campusSeleccionado || undefined,
        id_seccion: seccionId || undefined,
        id_ciclo_escolar: cicloId || undefined,
        search: busqueda || undefined,
      });
      const lista = data.grupos || [];
      setGrupos(lista);
      if (lista.length === 0) {
        setErrorMessage(
          campusSeleccionado
            ? 'No se encontraron grupos activos para los filtros seleccionados.'
            : 'Seleccione un campus en el encabezado para ver los grupos disponibles.'
        );
      }
    } catch (error) {
      console.error('[BusquedaGrupoCampus] Error:', error);
      setErrorMessage('Error al cargar los grupos. Intente nuevamente.');
      setGrupos([]);
    } finally {
      setLoading(false);
    }
  };

  // Calcular paginación
  const totalPaginas = Math.ceil(grupos.length / REGISTROS_POR_PAGINA);
  const indiceInicio = (paginaActual - 1) * REGISTROS_POR_PAGINA;
  const indiceFin = indiceInicio + REGISTROS_POR_PAGINA;
  const gruposPaginados = grupos.slice(indiceInicio, indiceFin);

  return (
    <div className={styles.busquedaContainer}>
      <div className={styles.busquedaCard}>
        <div className={styles.busquedaHeader}>
          <div className={styles.busquedaIcono}>
            <span>🔍</span>
          </div>
          <h2 className={styles.busquedaTitulo}>Búsqueda de Grupo</h2>
        </div>

        {/* Filtros: Campus, Sección Académica y Ciclo Escolar */}
        <div className={styles.filtrosContainer}>
          <div className={styles.filtroGroup}>
            <label className={styles.filtroLabel}>Campus</label>
            <select
              className={styles.filtroSelect}
              value={campusSeleccionado}
              disabled
            >
              <option value="">
                {campusSeleccionado ? `Campus ID: ${campusSeleccionado}` : 'Seleccione campus en el encabezado'}
              </option>
            </select>
            <small style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              (Seleccione el campus desde el menú global)
            </small>
          </div>
          <div className={styles.filtroGroup}>
            <label className={styles.filtroLabel}>Sección Académica</label>
            <select
              className={styles.filtroSelect}
              value={seccionId}
              onChange={(e) => setSeccionId(e.target.value)}
            >
              <option value="">-- Todas las secciones --</option>
              {secciones.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.filtroGroup}>
            <label className={styles.filtroLabel}>Ciclo Escolar</label>
            <select
              className={styles.filtroSelect}
              value={cicloId}
              onChange={(e) => setCicloId(e.target.value)}
            >
              <option value="">-- Todos los ciclos --</option>
              {ciclos.map((c) => {
                const cicloId = c.id || c.id_ciclo_escolar;
                return (
                  <option key={cicloId} value={cicloId}>
                    {c.nombre_ciclo || c.nombre}
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        {/* Barra de búsqueda */}
        <div className={styles.searchBarContainer}>
          <div className={styles.searchInputWrapper}>
            <svg
              className={styles.searchIcon}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Buscar Grupo..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleBuscar()}
            />
          </div>
          <button
            className={styles.buscarButton}
            onClick={handleBuscar}
            disabled={loading}
          >
            {loading ? 'BUSCANDO...' : 'BUSCAR'}
          </button>
        </div>

        {errorMessage && (
          <div className={styles.errorMessage}>{errorMessage}</div>
        )}
      </div>

      {(grupos.length > 0 || loading) && (
        <>
          <div className={styles.tablaContainer}>
            <table className={styles.tabla}>
              <thead>
                <tr className={styles.tablaHeader}>
                  <th>Grupo</th>
                  <th>Campus</th>
                  <th>Programa</th>
                  <th>Ciclo Escolar</th>
                  <th>Asesor</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className={styles.tablaCargando}>
                      Cargando grupos...
                    </td>
                  </tr>
                ) : (
                  gruposPaginados.map((grupo) => (
                    <tr
                      key={grupo.id_grupo}
                      className={styles.tablaFila}
                      onClick={() => onSeleccionarGrupo(grupo)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        {grupo.codigo} - {grupo.nombre}
                      </td>
                      <td>{grupo.campus}</td>
                      <td>
                        {grupo.programa_academico &&
                        grupo.programa_academico !== 'Sin programa'
                          ? grupo.programa_academico
                          : grupo.programa}
                      </td>
                      <td>{grupo.ciclo}</td>
                      <td>{grupo.asesor_nombre || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Controles de Paginación */}
          {grupos.length > 0 && (
            <div className={styles.paginationContainer}>
              <button
                className={styles.paginationButton}
                onClick={() => setPaginaActual(1)}
                disabled={paginaActual === 1}
              >
                ← Primera
              </button>
              <button
                className={styles.paginationButton}
                onClick={() => setPaginaActual(paginaActual - 1)}
                disabled={paginaActual === 1}
              >
                Anterior
              </button>

              {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                let numPagina = i + 1;
                if (totalPaginas > 5) {
                  if (paginaActual > 3) {
                    numPagina = paginaActual - 2 + i;
                  }
                  if (numPagina > totalPaginas) return null;
                }
                return (
                  <button
                    key={numPagina}
                    className={`${styles.paginationButton} ${paginaActual === numPagina ? styles.active : ''}`}
                    onClick={() => setPaginaActual(numPagina)}
                  >
                    {numPagina}
                  </button>
                );
              })}

              <button
                className={styles.paginationButton}
                onClick={() => setPaginaActual(paginaActual + 1)}
                disabled={paginaActual === totalPaginas}
              >
                Siguiente
              </button>
              <button
                className={styles.paginationButton}
                onClick={() => setPaginaActual(totalPaginas)}
                disabled={paginaActual === totalPaginas}
              >
                Última →
              </button>

              <span className={styles.paginationInfo}>
                Página {paginaActual} de {totalPaginas} | Mostrando {gruposPaginados.length} de {grupos.length} registros
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default BusquedaGrupoCampus;
